import { FC, useState } from 'react'
import { Pie, Cell, ResponsiveContainer, Sector } from 'recharts'
import { useCustomTheme } from '@/core/theme'
import { PieChartData, PieChartProps } from './PieChart.types'
import { RePieChart } from './PieChart.styles'

const ActiveShape = (props: any) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
        props
    return (
        <g>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + 1} // Slightly larger radius for hover effect
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                stroke={fill}
            />
        </g>
    )
}

const DEFAULT_COLORS = [
    (theme: any) => theme.colors.semantic.alertCritical,
    (theme: any) => theme.colors.semantic.alertHigh,
    (theme: any) => theme.colors.semantic.alertMedium,
    (theme: any) => theme.colors.semantic.alertLow,
    (theme: any) => theme.colors.semantic.alertInfo,
]

export const PieChart: FC<
    PieChartProps & { onSectionClick?: (name: string) => void }
> = ({
    data,
    width = 143,
    height = 143,
    innerRadius = 45,
    outerRadius = 70,
    paddingAngle = 2,
    startAngle = 90,
    endAngle = -450,
    stroke: strokeProp,
    strokeWidth = 1,
    style,
    emptyColor: emptyColorProp,
    isEmpty = false,
    onSectionClick,
}) => {
    const theme = useCustomTheme()
    const [activeIndex, setActiveIndex] = useState<number | undefined>(
        undefined
    )

    const stroke = strokeProp ?? theme.colors.greys[50]
    const emptyColor = emptyColorProp ?? theme.colors.greys[50]

    const getColor = (entry: PieChartData, index: number) => {
        if (entry.color) return entry.color
        if (DEFAULT_COLORS[index]) return DEFAULT_COLORS[index](theme)
        // fallback for alertInfo or generic gray
        return (
            (theme.colors.semantic as any).alertInfo ?? theme.colors.greys[50]
        )
    }

    const handleMouseEnter = (_: any, index: number) => {
        setActiveIndex(index)
    }

    const handleMouseLeave = () => {
        setActiveIndex(undefined)
    }

    return (
        <ResponsiveContainer width={width} height={height}>
            <RePieChart style={style}>
                {isEmpty && (
                    <Pie
                        data={[{ name: 'outer', value: 1 }]}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        startAngle={startAngle}
                        endAngle={endAngle}
                        innerRadius={outerRadius}
                        outerRadius={outerRadius + 2}
                        fill={emptyColor}
                        stroke="none"
                        animationDuration={700}
                    />
                )}
                <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={innerRadius}
                    outerRadius={outerRadius}
                    paddingAngle={paddingAngle}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    labelLine={false}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    animationDuration={700}
                    activeIndex={activeIndex}
                    activeShape={ActiveShape}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={
                        onSectionClick
                            ? (data, index) => {
                                  if (onSectionClick && data && data.name) {
                                      onSectionClick(data.name)
                                  }
                              }
                            : undefined
                    }
                >
                    {data.map((entry, index) => (
                        <Cell
                            key={`cell-${index}`}
                            fill={getColor(entry, index)}
                            onClick={
                                onSectionClick
                                    ? () => onSectionClick(entry.name)
                                    : undefined
                            }
                        />
                    ))}
                </Pie>
                {isEmpty && (
                    <Pie
                        data={[{ name: 'inner', value: 1 }]}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        startAngle={startAngle}
                        endAngle={endAngle}
                        innerRadius={innerRadius - 2}
                        outerRadius={innerRadius}
                        fill={emptyColor}
                        stroke="none"
                        animationDuration={700}
                    />
                )}
            </RePieChart>
        </ResponsiveContainer>
    )
}
